import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function B2BPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const buildingTypes = [
    {
      title: 'Stambene zgrade',
      desc: 'Kućni brojevi za ulazne porte, apartmanske oznake, brojevi spratova i interfon table.',
      icon: 'ri-building-2-line',
    },
    {
      title: 'Apartmani',
      desc: 'Elegantne oznake za apartmane, brojevi stanova, imena gostiju i personalizovani otirači.',
      icon: 'ri-home-5-line',
    },
    {
      title: 'Hoteli',
      desc: 'Sobne oznake, brojevi spratova, oznake za recepciju, pločice za vrata i koridor table.',
      icon: 'ri-hotel-line',
    },
    {
      title: 'Poslovni prostori',
      desc: 'Oznake za kancelarije, konferencijske sale, parking mesta i upozorenja.',
      icon: 'ri-briefcase-4-line',
    },
  ];

  const b2bProducts = [
    {
      name: 'Kućni brojevi za zgrade',
      price: 'od 1.000 RSD',
      image: 'https://readdy.ai/api/search-image?query=Modern%20apartment%20building%20entrance%20with%20elegant%20house%20number%20sign%20mounted%20on%20dark%20wall%2C%20brushed%20metal%20finish%2C%20minimalist%20design%2C%20warm%20evening%20light%2C%20high%20end%20architectural%20photography%20with%20soft%20shadows&width=600&height=400&seq=b2b-1&orientation=landscape',
      desc: 'Veće količine kućnih brojeva za zgrade sa popustom. Dostupno u 6 boja i 8 oblika.',
    },
    {
      name: 'Apartmanske oznake',
      price: 'od 1.200 RSD',
      image: 'https://readdy.ai/api/search-image?query=Stylish%20apartment%20door%20number%20sign%20with%20modern%20typography%20on%20wooden%20door%2C%20warm%20interior%20light%2C%20cozy%20hallway%20background%2C%20editorial%20interior%20photography%20with%20soft%20natural%20light&width=600&height=400&seq=b2b-2&orientation=landscape',
      desc: 'Elegantne oznake za apartmane i stanove. Mogućnost graviranja imena i brojeva.',
    },
    {
      name: 'Pločice za vrata',
      price: 'od 1.800 RSD',
      image: 'https://readdy.ai/api/search-image?query=Premium%20door%20name%20plate%20on%20modern%20office%20door%2C%20brushed%20brass%20finish%20with%20elegant%20engraving%2C%20clean%20minimalist%20corridor%2C%20architectural%20photography%20with%20soft%20diffused%20lighting&width=600&height=400&seq=b2b-3&orientation=landscape',
      desc: 'Profesionalne pločice za vrata sa imenom i titulom. Idealno za hotele i poslovne zgrade.',
    },
    {
      name: 'Oznake za parking',
      price: 'od 900 RSD',
      image: 'https://readdy.ai/api/search-image?query=Private%20parking%20sign%20mounted%20on%20concrete%20wall%20in%20underground%20garage%2C%20modern%20metal%20sign%20with%20clean%20typography%2C%20subtle%20warm%20lighting%20from%20above%2C%20editorial%20architectural%20photography&width=600&height=400&seq=b2b-4&orientation=landscape',
      desc: 'Zabranjeno parkiranje, rezervisano parking mesto i druge parking oznake.',
    },
    {
      name: 'Čuvaj se psa',
      price: 'od 800 RSD',
      image: 'https://readdy.ai/api/search-image?query=Premium%20beware%20of%20dog%20sign%20on%20modern%20metal%20gate%2C%20elegant%20minimalist%20design%20with%20brushed%20steel%20finish%2C%20green%20garden%20background%20blurred%2C%20warm%20afternoon%20light%2C%20product%20photography&width=600&height=400&seq=b2b-5&orientation=landscape',
      desc: 'Elegantne oznake "Čuvaj se psa" za zgrade sa ogradama. Različite dimenzije.',
    },
    {
      name: 'Video nadzor oznake',
      price: 'od 800 RSD',
      image: 'https://readdy.ai/api/search-image?query=Modern%20security%20camera%20sign%20mounted%20on%20dark%20wall%20near%20surveillance%20camera%2C%20sleek%20metal%20plate%20with%20clean%20typography%2C%20subtle%20red%20accent%20light%2C%20professional%20security%20photography%20with%20moody%20lighting&width=600&height=400&seq=b2b-6&orientation=landscape',
      desc: 'Oznake za objekte pod video nadzorom. Za zgrade, poslovne prostore i apartmane.',
    },
  ];

  const benefits = [
    {
      icon: 'ri-price-tag-3-line',
      title: 'Popusti na veće količine',
      desc: '10-30% popusta u zavisnosti od količine. Što više poručite, veći je popust.',
    },
    {
      icon: 'ri-time-line',
      title: 'Brza izrada i isporuka',
      desc: 'Izrada do 7 dana za količine do 100 komada. Dostava širom Srbije i regiona.',
    },
    {
      icon: 'ri-palette-line',
      title: 'Potpuna personalizacija',
      desc: 'Boje, dimenzije, fontovi i dizajn po vašim specifikacijama. Logo i brendiranje.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Garancija i kvalitet',
      desc: 'Sve oznake od nerdjajućeg čelika ili aluminijuma. 5 godina garancije.',
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20luxury%20apartment%20building%20exterior%20at%20golden%20hour%20with%20elegant%20house%20number%20signs%20on%20entrance%2C%20warm%20architectural%20lighting%2C%20clean%20minimalist%20design%2C%20premium%20real%20estate%20photography%20with%20soft%20shadows%20and%20organic%20composition&width=1600&height=900&seq=b2b-hero&orientation=landscape"
            alt="Proizvodi za firme i zgrade"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 md:px-6 max-w-[1450px] mx-auto">
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4 max-w-4xl">
              Za Firme & Zgrade
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mb-6 leading-relaxed">
              Veće količine kućnih brojeva, apartmanskih oznaka i pločica za zgrade, hotele i poslovne prostore. Personalizovano po vašim specifikacijama.
            </p>
            <a
              href="#upit"
              className="px-8 py-3 bg-amber-600 text-white font-label text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap"
            >
              Pošalji upit
            </a>
          </div>
        </section>

        {/* Building Types */}
        <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background-50 overflow-hidden">
          <div className="w-full px-4 md:px-6 max-w-[1450px] mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                Za svaki tip objekta
              </h2>
              <p className="reveal opacity-0 translate-y-4 transition-all duration-700 delay-100 ease-out text-base text-foreground-700 leading-relaxed">
                Nudimo rešenja za sve vrste objekata — od stambenih zgrada i apartmana do hotela i poslovnih prostora.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {buildingTypes.map((type, i) => (
                <div
                  key={type.title}
                  className="reveal opacity-0 translate-y-4 transition-all duration-700 ease-out p-6 bg-white rounded-lg border border-background-200 hover:border-amber-300 transition-colors"
                  style={{ transitionDelay: `${(i + 2) * 100}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-lg mb-4">
                    <i className={`${type.icon} text-amber-600 text-xl`} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                    {type.title}
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {type.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products for B2B */}
        <section className="w-full py-16 md:py-24 bg-background-100 overflow-hidden">
          <div className="w-full px-4 md:px-6 max-w-[1450px] mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                Proizvodi za firme
              </h2>
              <p className="text-base text-foreground-700 leading-relaxed">
                Sve što vam je potrebno za označavanje objekata. Poručite veće količine sa popustom.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {b2bProducts.map((product, i) => (
                <div
                  key={product.name}
                  className="group bg-white rounded-lg overflow-hidden border border-background-200 hover:border-amber-300 transition-all duration-300"
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">
                      {product.name}
                    </h3>
                    <p className="text-sm text-foreground-700 mb-3 leading-relaxed">
                      {product.desc}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-label font-semibold text-amber-600">
                        {product.price}
                      </span>
                      <span className="text-xs font-label text-foreground-600 bg-background-100 px-2 py-1 rounded">
                        Veće količine = popust
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="w-full py-16 md:py-24 bg-[#1a1a1a] overflow-hidden">
          <div className="w-full px-4 md:px-6 max-w-[1450px] mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                Zašto firme biraju nas
              </h2>
              <p className="text-base text-white/70 leading-relaxed">
                Preko 500 firmi, investitora i upravnika zgrada je prepoznalo kvalitet naših proizvoda.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10"
                >
                  <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-amber-600/20 rounded-full">
                    <i className={`${benefit.icon} text-amber-400 text-2xl`} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {benefit.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA / Contact Form */}
        <section id="upit" className="w-full py-16 md:py-24 bg-background-50 overflow-hidden">
          <div className="w-full px-4 md:px-6 max-w-[1450px] mx-auto">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                  Pošalji upit za veće količine
                </h2>
                <p className="text-base text-foreground-700 leading-relaxed">
                  Popunite formu ispod i naš tim će vam poslati ponudu u roku od 24 sata. Nudimo specijalne cene za veće količine.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-background-200 p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                      Ime i prezime
                    </label>
                    <input
                      type="text"
                      placeholder="Vaše ime"
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="vas@email.com"
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                      Naziv firme
                    </label>
                    <input
                      type="text"
                      placeholder="Naziv firme"
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      placeholder="+381 xx xxx xxxx"
                      className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                    Tip objekta
                  </label>
                  <select className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 focus:outline-none focus:border-amber-500 transition-colors">
                    <option>Izaberite tip objekta</option>
                    <option>Stambena zgrada</option>
                    <option>Apartmani</option>
                    <option>Hotel</option>
                    <option>Poslovni prostor</option>
                    <option>Drugo</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                    Količina
                  </label>
                  <select className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 focus:outline-none focus:border-amber-500 transition-colors">
                    <option>Izaberite približnu količinu</option>
                    <option>10-30 komada</option>
                    <option>31-100 komada</option>
                    <option>101-500 komada</option>
                    <option>500+ komada</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-label font-medium text-foreground-800 mb-1">
                    Poruka
                  </label>
                  <textarea
                    placeholder="Opišite šta vam je potrebno — dimenzije, boje, fontovi, logo..."
                    rows={4}
                    className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-lg text-sm font-label text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-amber-500 transition-colors resize-none"
                  />
                </div>

                <button
                  type="button"
                  className="w-full px-6 py-3 bg-foreground-950 text-white font-label text-sm font-medium rounded-lg hover:bg-foreground-900 transition-colors"
                >
                  Pošalji upit
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}